/**
 * German relative-time helpers for ticket timestamps.
 *
 * `relativeTime(iso)` → e.g. "gerade eben", "vor 5 Min.", "vor 3 Std.",
 * "vor 2 Tagen", "vor 4 Wochen", "vor 2 Monaten", "vor 1 Jahr".
 * `daysOpen(iso)` → integer number of whole days since the given timestamp.
 *
 * Beide Funktionen nehmen einen optionalen `now`-Zeitstempel (Default
 * `Date.now()`) statt intern `Date.now()` aufzurufen — sonst kippen Tests, die
 * relative Zeiten prüfen, irgendwann von selbst (s. CLAUDE.md Gotcha „Tests,
 * die den heutigen Tag lesen").
 */

const MINUTE = 60 * 1000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;
const WEEK = 7 * DAY;
const MONTH = 30 * DAY;
const YEAR = 365 * DAY;

/** Human-readable German "vor …" string for a past ISO timestamp. */
export function relativeTime(iso: string, now: number = Date.now()): string {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "";
  const diff = now - then;
  if (diff < 0) return "gerade eben";
  if (diff < MINUTE) return "gerade eben";
  if (diff < HOUR) {
    const m = Math.floor(diff / MINUTE);
    return `vor ${m} Min.`;
  }
  if (diff < DAY) {
    const h = Math.floor(diff / HOUR);
    return `vor ${h} Std.`;
  }
  if (diff < WEEK) {
    const d = Math.floor(diff / DAY);
    return d === 1 ? "vor 1 Tag" : `vor ${d} Tagen`;
  }
  if (diff < MONTH) {
    const w = Math.floor(diff / WEEK);
    return w === 1 ? "vor 1 Woche" : `vor ${w} Wochen`;
  }
  if (diff < YEAR) {
    const mo = Math.floor(diff / MONTH);
    return mo === 1 ? "vor 1 Monat" : `vor ${mo} Monaten`;
  }
  const y = Math.floor(diff / YEAR);
  return y === 1 ? "vor 1 Jahr" : `vor ${y} Jahren`;
}

/** Whole days since the given ISO timestamp (>= 0). */
export function daysOpen(iso: string, now: number = Date.now()): number {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return 0;
  const diff = now - then;
  return Math.max(0, Math.floor(diff / DAY));
}

/** Absolute German date/time for use in `title=` tooltips. */
export function absoluteDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleString("de-DE", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}
